import { v4 as uuid } from 'uuid';
import type { LocalViewId, ViewKind } from '@jira/polaris__domain-view/src/ViewKind.types.tsx';
import { experience } from '@jira/polaris__lib-analytics/src/ConstantsExperience.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import type { Props, State } from './NavigationView.types.tsx';
import { withRefreshBlocker } from './withRefreshBlocker.tsx';
import {
	modifyView,
	updateContainerState,
	transformRemoteView,
	createFakeId,
	findView,
	makeNewTitle,
} from './updateContainerState.tsx';

export const cloneView = (viewId: LocalViewId, viewType: ViewKind): Action<State, Props> =>
	withRefreshBlocker(
		async (
			{ getState, setState },
			{ navigationRemote, cloudId, containerId, onFailure, onSuccess, onViewCreated },
		) => {
			if (
				!cloudId ||
				!containerId ||
				!navigationRemote ||
				getState().containers[containerId]?.isLoading ||
				!getState().containers[containerId]?.initialized
			) {
				return;
			}

			const { view, parent } = findView(getState().containers[containerId], viewId);
			if (!view) {
				return;
			}

			if (viewType === 'SECTION') {
				return;
			}

			experience.navBar.createView.start();

			const newViewId = createFakeId();
			const name = makeNewTitle(view.name);
			const newView = {
				id: newViewId,
				polarisId: 0,
				localId: uuid(),
				name,
				viewType,
				views: undefined,
				isLocked: true,
				isManuallyCreated: true,
			};

			if (parent) {
				setState(
					updateContainerState(getState(), containerId, {
						views: getState().containers[containerId].views.map((innerView) =>
							innerView.localId === parent.localId
								? {
										...innerView,
										views: [...(innerView.views || []), newView],
									}
								: innerView,
						),
					}),
				);
			} else {
				setState(
					updateContainerState(getState(), containerId, {
						views: [...getState().containers[containerId].views, newView],
					}),
				);
			}

			try {
				const remoteView = await navigationRemote.cloneView({
					id: view.id,
					viewType,
					name,
				});

				setState(
					modifyView(getState(), containerId, (innerView) =>
						innerView.localId === newView.localId
							? {
									...transformRemoteView(remoteView),
									localId: newView.localId,
									isManuallyCreated: true,
									isLocked: false,
								}
							: innerView,
					),
				);
				onSuccess?.('cloneView');
				const navView = findView(getState().containers[containerId], newView.localId).view;
				if (navView) {
					onViewCreated?.(navView);
				}

				experience.navBar.createView.success();

				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				setState(
					modifyView(getState(), containerId, (innerView) =>
						innerView.localId === newView.localId ? { ...innerView, isLocked: false } : innerView,
					),
				);

				onFailure?.(error, 'cloneView');

				if (isClientFetchError(error)) {
					experience.navBar.createView.abort(error);
					return;
				}

				experience.navBar.createView.failure(error);
			}
		},
	);
